import * as React from 'react';
import { ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './dropdown-menu';
import { Button } from './button';
import { cn } from '../../lib/utils';

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  /** Controlled selection, as option values. */
  selected: string[];
  onSelectedChange: (_values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

/**
 * Multi-select picker: a trigger button showing a selection count, opening a
 * menu of toggleable options that stays open while picking, with a Done
 * action to close. Replaces the legacy Select's isMulti/showDoneButton mode.
 */
export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onSelectedChange,
  placeholder = 'Select…',
  disabled,
  'aria-label': ariaLabel,
  className,
}) => {
  const [open, setOpen] = React.useState(false);

  const toggle = (value: string) => {
    onSelectedChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn('justify-between font-normal', className)}
        >
          <span className="truncate text-muted-foreground">
            {selected.length > 0 ? `${selected.length} selected` : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-72 overflow-y-auto" align="start">
        {options.map((option) => {
          const isSelected = selected.includes(option.value);
          return (
            <DropdownMenuItem
              key={option.value}
              role="menuitemcheckbox"
              aria-checked={isSelected}
              onSelect={(e) => {
                // Keep the menu open while toggling.
                e.preventDefault();
                toggle(option.value);
              }}
            >
              <span
                className={cn(
                  'mr-2 flex h-4 w-4 items-center justify-center rounded border',
                  isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                )}
              >
                {isSelected && <Check className="h-3 w-3" />}
              </span>
              {option.label}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="justify-center font-medium" onSelect={() => setOpen(false)}>
          Done
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
